import { FC, useEffect, useState } from "react";
import { PlacementDoc } from "../../../models/placements";
import { Tender } from "../../../models/tendering";
import { Dialog, DialogSurface, Title2, Title3, Text, DialogContent, Subtitle1, DialogActions, Button, Radio, RadioGroup, Field, Input, Textarea, Caption1, Spinner } from "@fluentui/react-components";
import { updatePlacement } from "../../../services/placementService";
import { useAppContext } from "../../../AppContext";

interface ContactTenderProps{
    isOpen:boolean;
    onClose: () => void;
    tender: Tender;
    tenderList: Tender[];
    placement:PlacementDoc;
    placementDocID: string;
    onPlacementUpdate?: (updatedPlacement: PlacementDoc) => void;
}

export const DenyTender: FC<ContactTenderProps> = ({
  isOpen,
  onClose,
  tender,
  tenderList,
  placement,
  placementDocID,
  onPlacementUpdate
}) => {
  const options = ["Matchar ej", "Fel målgrupp", "Fel avstånd", "Verksamhet tackade nej", "Övrigt"];
  const [option, setOption] = useState<string | null>(null);
  const [denyComment, setDenyComment] = useState<string | null>(null);
  const [error, setError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const {user} = useAppContext();

  useEffect(() => {
    setDenyComment(null);
    setError(false);
    setOption(null);
  }, [isOpen]);

  const saveChanges = async () => {
    if(!denyComment){
      setError(true);
      return;
    }
    
    setError(false);
    setIsLoading(true);
    
    try {
      const index = tenderList.findIndex(t => t.tenderID === tender.tenderID);
      if (index === -1) {
        setError(true);
        return;
      }

      // Create immutable copies to trigger proper React re-renders
      let updatedTenders = placement.tenders.map((t, i) => {
        if (i === index) {
          return { ...t, denied: true, denyComment: denyComment };
        }
        return { ...t };
      });

      // Update rankingTarget if needed
      const wasRankingTarget = placement.tenders[index].rankingTarget;
      if (wasRankingTarget) {
        updatedTenders = updatedTenders.map((t, i) => {
          if (i === index) {
            return { ...t, rankingTarget: false };
          }
          return t;
        });

        // When the request is closed (deadline passed on a sent placement),
        // only Accepted tenders are valid ranking targets — not Pending ones.
        const isRequestOpen = new Date(placement.answerDate) > new Date() && placement.status === 'sent';
        const isClosedSent = placement.status === 'sent' && !isRequestOpen;

        // Find next valid tender for rankingTarget
        for (let i = index + 1; i < updatedTenders.length; i++) {
          const t = updatedTenders[i];
          const isValidTarget = !t.denied && (
            isClosedSent
              ? (t.placementRequestReceiverStatus === 'Accepted' || t.placementRequestReceiverStatus === 'Pending')
              : t.placementRequestReceiverStatus !== 'Rejected'
          );
          if (isValidTarget) {
            updatedTenders = updatedTenders.map((tender, idx) => {
              if (idx === i) {
                return { ...tender, rankingTarget: true };
              }
              return tender;
            });
            break;
          }
        }
      }

      const updatedPlacement: PlacementDoc = {
        ...placement,
        tenders: updatedTenders,
        logs: {
          ...placement.logs,
          tenderChanges: [
            ...placement.logs.tenderChanges,
            {
              date: new Date().toISOString(),
              tenderChange: "Verksamhet väljs bort",
              tender: placement.tenders[index].tenderName,
              comment: denyComment,
              userName: user.user.firstName + " " + user.user.lastName,
              filters: placement.activeFilters.filters
            }
          ]
        }
      };
      
      await updatePlacement(updatedPlacement, placementDocID);
      if (onPlacementUpdate) {
        onPlacementUpdate(updatedPlacement);
      }
      onClose();
    } catch (error) {
      console.error('Error denying tender:', error);
      setError(true);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={(event, data) => !data.open && !isLoading && onClose()}>
      <DialogSurface>
        <DialogContent className="flex flex-col gap-2">
            <Subtitle1>Välj bort</Subtitle1>
            <Text weight="semibold">Anledning/kommentar för borttagning:</Text>
            <Field required>
              <RadioGroup disabled={isLoading}>
              {options.map(option => (
                <Radio 
                  key={option} 
                  value={option} 
                  label={option} 
                  disabled={isLoading}
                  onChange={(e) => {
                    setOption(e.target.value);
                    if (e.target.value !== "Övrigt") {
                      setDenyComment(e.target.value);
                    }
                    else {
                      setDenyComment(null);
                    }
                  }} 
                />
              ))}
              </RadioGroup>
            </Field>
            {option == "Övrigt" && (
              <Field required>
                <Textarea 
                  resize="vertical" 
                  disabled={isLoading}
                  onChange={(e) => setDenyComment(e.target.value)} 
                />
              </Field>
            )}
        </DialogContent>
        <DialogActions className="mt-8">
            <Button 
              onClick={onClose}
              disabled={isLoading}
            >
              Avbryt
            </Button>
            <Button 
              appearance="primary" 
              onClick={saveChanges}
              disabled={isLoading}
              icon={isLoading ? <Spinner size="tiny" /> : undefined}
            >
              {isLoading ? "Sparar..." : "Välj bort"}
            </Button>
        </DialogActions>
        <DialogActions className="mt-2">
            {error && (
              <Caption1 className="text-red-500">
                {isLoading ? "Ett fel uppstod vid sparande" : "Måste välja ett alternativ"}
              </Caption1>
            )}
        </DialogActions>
      </DialogSurface>
    </Dialog>
  );
};

export default DenyTender;